import React from 'react';
import { Modal } from 'react-bootstrap';
import { FaCheckCircle, FaExclamationTriangle, FaTimesCircle, FaLink, FaClock, FaHistory, FaServer } from 'react-icons/fa';

/**
 * @component ServiceDetailModal
 * @description Modal to show detailed health metrics for a specific service.
 * @param {boolean} show - Whether the modal is visible.
 * @param {Object} data - The service detail data object from API.
 * @param {Function} onClose - Handler to close the modal.
 */
export default function ServiceDetailModal({ show, data, loading, onClose }) {
    // Only extract data if it exists
    const current_status = data?.current_status;
    const metrics = data?.metrics;
    const status = current_status?.status || "Unknown";
    const statusLower = status.toLowerCase();

    // Determine status color/icon
    let statusVariant = "secondary";
    let StatusIcon = FaServer;

    if (statusLower === "operational" || statusLower === "success" || statusLower === "healthy") {
        statusVariant = "success";
        StatusIcon = FaCheckCircle;
    } else if (statusLower === "degraded" || statusLower === "warning") {
        statusVariant = "warning";
        StatusIcon = FaExclamationTriangle;
    } else if (statusLower === "outage" || statusLower === "error" || statusLower === "danger") {
        statusVariant = "danger";
        StatusIcon = FaTimesCircle;
    }

    return (
        <Modal show={show} onHide={onClose} size="lg" centered contentClassName="border-0 shadow-lg">
            <Modal.Header closeButton className="border-bottom-0 pb-0">
                <Modal.Title className="fw-bold d-flex align-items-center">
                    {loading ? (
                        <>
                            <FaServer className="text-secondary me-2" />
                            <span>Loading...</span>
                        </>
                    ) : (data ? (
                        <>
                            <StatusIcon className={`text-${statusVariant} me-2`} />
                            {data.service_name}
                        </>
                    ) : (
                        <span>Service Details</span>
                    ))}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4">
                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="text-muted mt-3 mb-0">Fetching service health metrics...</p>
                    </div>
                ) : !data ? (
                    <div className="text-center py-5 text-muted">
                        <p>No details available.</p>
                    </div>
                ) : (
                    <>
                        {/* Meta Info */}
                        <div className="d-flex flex-wrap gap-4 mb-4 text-muted small">
                            <div className="d-flex align-items-center gap-2">
                                <FaLink />
                                <span className="font-monospace">{data.endpoint}</span>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <FaHistory />
                                <span className="text-capitalize">{data.time_period} View</span>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <FaClock />
                                <span>Last Checked: {new Date(data.timestamp).toLocaleTimeString()}</span>
                            </div>
                        </div>

                        {/* Status Alert */}
                        <div className={`alert alert-${statusVariant} d-flex align-items-start gap-3 mb-4`}>
                            <StatusIcon className="mt-1 flex-shrink-0" size={20} />
                            <div>
                                <h6 className="fw-bold text-capitalize mb-1">{status}</h6>
                                <div className="small font-monospace text-wrap" style={{ wordBreak: 'break-all' }}>
                                    {current_status?.error || "All systems operational."}
                                </div>
                            </div>
                        </div>

                        {/* Metrics Grid */}
                        <div className="row g-3">
                            <div className="col-md-6">
                                <div className="p-3 bg-light rounded border h-100">
                                    <div className="text-muted small fw-bold text-uppercase mb-1">Avg Latency</div>
                                    <h3 className="fw-bold mb-0">
                                        {metrics?.avg_latency_ms?.toFixed(2)}
                                        <span className="fs-6 text-muted ms-1">ms</span>
                                    </h3>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="p-3 bg-light rounded border h-100">
                                    <div className="text-muted small fw-bold text-uppercase mb-1">Uptime</div>
                                    <h3 className="fw-bold mb-0">
                                        {metrics?.uptime_percentage?.toFixed(2)}
                                        <span className="fs-6 text-muted ms-1">%</span>
                                    </h3>
                                </div>
                            </div>
                        </div>

                        {data.logs && data.logs.length > 0 && (
                            <div className="mt-4">
                                <h6 className="fw-bold text-dark mb-3">Recent Logs</h6>
                                <div className="bg-dark text-white p-3 rounded font-monospace small" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                    {data.logs.map((log, i) => (
                                        <div key={i} className="mb-1 border-bottom border-secondary pb-1 last-border-0">
                                            <span className="text-muted me-2">[{new Date(log.timestamp).toLocaleTimeString()}]</span>
                                            <span className={log.level === 'error' ? 'text-danger' : 'text-light'}>{log.message}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}

            </Modal.Body>
        </Modal>
    );
}
